<template>
  <div class="review-lhb h-100 d-flex flex-column bg-main">
    <!-- Header / Date Picker -->
    <div class="p-2 border-bottom bg-card d-flex align-items-center justify-content-between shadow-sm" style="z-index: 10;">
      <div class="d-flex align-items-center">
        <span class="font-weight-bold mr-2 text-main" style="font-size: 1.1rem; white-space: nowrap;">龙虎榜</span>

        <b-form-datepicker
          v-model="date1"
          locale="zh"
          class="mr-2 border-0"
          size="sm"
          :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' ,weekday:'short'}"
          style="max-width: 200px;"
          :max="new Date()"
        ></b-form-datepicker>
      </div>
      <div class="d-flex align-items-center">
        <b-button-group size="sm" class="mr-2">
          <b-button :variant="viewMode === 'list' ? 'primary' : 'outline-primary'" @click="viewMode = 'list'">列表</b-button>
          <b-button :variant="viewMode === 'mindmap' ? 'primary' : 'outline-primary'" @click="viewMode = 'mindmap'">脑图</b-button>
        </b-button-group>
        <b-button variant="outline-primary" size="sm" @click="fetchList" class="border-0 mr-2">
          <b-icon icon="arrow-clockwise"></b-icon>
        </b-button>
        <WxShare :title="shareTitle" :desc="shareDesc" :link="shareLink" />
      </div>
    </div>

    <!-- Seat Filter (Global) -->
    <div v-if="allSeats.length > 0" class="border-bottom bg-card flex-shrink-0">

        <div :style="filterStyle" class="p-2">
            <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="font-weight-bold small">交易席位过滤 ({{ selectedSeats.length }}/{{ allSeats.length }})</span>
                <div class="d-flex align-items-center">
                    <b-form-checkbox 
                        v-if="!isMobile" 
                        v-model="showBuySellCounts" 
                        switch 
                        size="sm" 
                        class="mr-3"
                    >
                        买卖数
                    </b-form-checkbox>
                    <b-form-checkbox
                        :checked="allSelected"
                        :indeterminate="indeterminate"
                        @change="toggleAll"
                        size="sm"
                        class="mr-2"
                    >
                        全选
                    </b-form-checkbox>
                    <b-button size="sm" variant="outline-primary" class="py-0 px-2" @click.stop="downloadTable">下载</b-button>
                </div>
            </div>
            <div style="max-height: 150px; overflow-y: auto;" class="d-flex flex-wrap">
                    <b-form-checkbox-group
                    v-model="selectedSeats"
                    :options="allSeats"
                    size="sm"
                    class="seat-filter-group w-100"
                    :class="{'seat-filter-mobile': isMobile}"
                    :style="isMobile ? 'column-count: 4; column-gap: 0.25rem;' : 'column-count: 4; column-gap: 1rem;'" 
                ></b-form-checkbox-group>
            </div>
        </div>
    </div>

    <div class="flex-grow-1 d-flex position-relative" :class="{'overflow-auto': viewMode === 'mindmap', 'overflow-hidden': viewMode !== 'mindmap'}">
      <!-- List View (Left Column on PC, Full Width on Mobile) -->
      <div 
        v-if="viewMode === 'list'"
        class="list-panel h-100 overflow-auto bg-card w-100" 
      >
        <div :style="isMobile ? 'min-width: 825px;' : 'max-width: 1200px; margin: 0 auto; min-height: 100%;'" :class="{'border-left border-right shadow-sm': !isMobile}">
            <!-- Statistics Bar -->
            <div v-if="statistics" class="d-flex flex-wrap border-bottom bg-card p-2 small text-secondary align-items-center sticky-top" style="top: 0; z-index: 6;">
                <div class="mr-3">
                    <span>净买入累计: </span>
                    <span :class="getColorClass(statistics.netBuy)" class="font-weight-bold">{{ formatMoney(statistics.netBuy) }}</span>
                </div>
                <div class="mr-3">
                    <span>成交额均值/累计: </span>
                    <span class="text-main font-weight-bold">{{ formatMoney(statistics.avgTurnover) }} / {{ formatMoney(statistics.turnover) }}</span>
                </div>
                <div class="mr-3">
                    <span>流通/总市值均值: </span>
                    <span class="text-main font-weight-bold">{{ formatMoney(statistics.avgCircCap) }} / {{ formatMoney(statistics.avgTotalCap) }}</span>
                </div>
                <div class="mr-3">
                    <span>流通/总市值累计: </span>
                    <span class="text-main font-weight-bold">{{ formatMoney(statistics.circCap) }} / {{ formatMoney(statistics.totalCap) }}</span>
                </div>
        
                
            </div>

            <!-- List Header -->
            <div class="d-flex border-bottom bg-muted text-secondary small p-2 sticky-top" style="z-index: 5; top: 37px;">
                <div :style="getColStyle('name')">股票名称</div>
                <div :style="getColStyle('seats')">交易席位</div>
                <div :style="getColStyle('concepts')">概念</div>
                <div :style="getColStyle('gain')" class="text-right sortable" @click="toggleSort('gain')">
                    涨幅
                    <b-icon v-if="sortKey === 'gain'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </div>
                <div :style="getColStyle('netbuy')" class="text-right sortable" @click="toggleSort('netbuy')">
                    净买入
                    <b-icon v-if="sortKey === 'netbuy'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </div>
                <div :style="getColStyle('turnover')" class="text-right sortable" @click="toggleSort('turnover')">
                    成交额
                    <b-icon v-if="sortKey === 'turnover'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </div>
                <div :style="getColStyle('total_cap')" class="text-right sortable" @click="toggleSort('total_cap')">
                    总市值
                    <b-icon v-if="sortKey === 'total_cap'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </div>
                <div :style="getColStyle('circ_cap')" class="text-right sortable" @click="toggleSort('circ_cap')">
                    流通市值
                    <b-icon v-if="sortKey === 'circ_cap'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </div>
                <div :style="getColStyle('amp')" class="text-right sortable" @click="toggleSort('amp')">
                    振幅
                    <b-icon v-if="sortKey === 'amp'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </div>
                <div :style="getColStyle('rate')" class="text-right sortable" @click="toggleSort('rate')">
                    换手率
                    <b-icon v-if="sortKey === 'rate'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                </div>
            </div>

            <div v-if="loading" class="text-center p-5">
                <b-spinner small variant="secondary"></b-spinner>
            </div>

            <div v-else-if="filteredLhbList.length === 0" class="text-center p-5 text-muted">
                暂无数据
            </div>
            
            <div 
                v-for="item in filteredLhbList" 
                :key="item.stock_code" 
                class="lhb-list-item p-2 border-bottom cursor-pointer"
                :class="{'bg-muted': selectedStock && selectedStock.stock_code === item.stock_code}"
                @click="selectStock(item)"
            >
                <div class="d-flex align-items-center" style="min-height: 50px;">
                    <!-- Col 1: Name/Code -->
                    <div :style="getColStyle('name')" style="padding-right: 5px;">
                        <div class="d-flex align-items-center flex-wrap">
                            <span class="font-weight-bold text-main text-truncate mr-1" style="font-size: 0.9rem;">{{ item.stock_name }}</span>

                            <!-- T Badge -->
                            <span v-if="item.t_type === 1" class="badge badge-danger scale-8 px-1 mr-1" style="line-height: 1;">T</span>
                            <!-- TIcon -->
                            <span v-if="item.t_icon" class="badge badge-danger scale-8 px-1" style="line-height: 1;">{{ item.t_icon }}T</span>
                        </div>
                        <div class="mt-1 d-flex align-items-center flex-wrap">
                            <span class="text-secondary small mr-1" style="font-size: 0.75rem;">{{ item.stock_code }}</span>
                            <!-- 3-Day Badge -->
                            <span v-if="item.d3 > 0" class="text-danger border border-danger small px-1 scale-8 mr-1" style="line-height: 1; border-radius: 2px;">3日</span>
                        </div>
                        <!-- Up Reason / Limit Up Analysis -->
                        <!-- <div v-if="item.up_reason" class="mt-1 text-secondary text-truncate" style="font-size: 0.75rem; color: #999; max-width: 100%;" :title="item.up_reason">
                            {{ item.up_reason }}
                        </div> -->
                    </div>

                    <!-- Col 2: Trading Seats (group_icon) -->
                    <div :style="getColStyle('seats')" style="padding-right: 5px;">
                        <div class="d-flex flex-column align-items-start justify-content-center" style="gap: 2px;">
                            <!-- Buy seats (red border) -->
                            <span v-for="(icon, idx) in item.buy_group_icons" :key="'buy'+idx" 
                                class="badge bg-card text-danger border-danger small px-1 scale-8 mb-1" 
                                :class="{'d-flex flex-column align-items-start': isMobile, 'text-truncate': !isMobile}"
                                style="border-width: 1px; border-style: solid; max-width: 100%; height: auto;">
                                <span :class="{'text-truncate w-100 text-left': isMobile}">{{ icon.name }}</span>
                                <span :class="{'ml-0': isMobile, 'ml-1': !isMobile}" style="opacity: 0.8;">+{{ formatMoney(icon.amount) }}</span>
                            </span>
                            <!-- Sell seats (green border) -->
                            <span v-for="(icon, idx) in item.sell_group_icons" :key="'sell'+idx" 
                                class="badge bg-card text-success border-success small px-1 scale-8 mb-1" 
                                :class="{'d-flex flex-column align-items-start': isMobile, 'text-truncate': !isMobile}"
                                style="border-width: 1px; border-style: solid; max-width: 100%; height: auto;">
                                <span :class="{'text-truncate w-100 text-left': isMobile}">{{ icon.name }}</span>
                                <span :class="{'ml-0': isMobile, 'ml-1': !isMobile}" style="opacity: 0.8;">-{{ formatMoney(icon.amount) }}</span>
                            </span>
                        </div>
                    </div>


                    <!-- Col 3: Concepts -->
                    <div :style="getColStyle('concepts')" style="padding-right: 5px;">
                        <div class="d-flex flex-column align-items-start justify-content-center">
                            <span v-for="(concept, idx) in getDisplayConcepts(item.concepts)" :key="'c'+idx" 
                                class="badge badge-light border text-secondary small mb-1 text-truncate" style="max-width: 100%;">
                                {{ concept }}
                            </span>
                        </div>
                    </div>

                    <!-- Col: Increase -->
                    <div :style="getColStyle('gain')" class="text-right">
                        <div class="font-weight-bold" :class="getColorClass(item.quote_change)" style="font-size: 0.9rem;">
                            {{ item.quote_change }}%
                        </div>
                        <div v-if="item.up_desc" class="text-secondary text-truncate" style="font-size: 0.7rem; margin-top: 2px;" :title="item.up_desc">
                            {{ item.up_desc }}
                        </div>
                    </div>

                    <!-- Col: Net Buy -->
                    <div :style="getColStyle('netbuy')" class="text-right">
                        <div class="font-weight-bold" :class="getColorClass(item.buy_in)" style="font-size: 0.9rem;">
                            {{ formatMoney(item.buy_in) }}
                        </div>
                    </div>

                    <!-- New Cols: Turnover, Cap, Circ, Amp, Rate -->
                    <div :style="getColStyle('turnover')" class="text-right small">
                        {{ formatMoney(item.turnover) }}
                    </div>
                    <div :style="getColStyle('total_cap')" class="text-right small">
                        {{ formatMoney(item.capitalization) }}
                    </div>
                    <div :style="getColStyle('circ_cap')" class="text-right small">
                        {{ formatMoney(item.circ_price) }}
                    </div>
                    <div :style="getColStyle('amp')" class="text-right small">
                        {{ item.amplitude }}%
                    </div>
                    <div :style="getColStyle('rate')" class="text-right small">
                        {{ item.turnover_ratio }}%
                    </div>
                </div>
            </div>
        </div>
      </div>

      <!-- Mind Map View -->
      <div v-if="viewMode === 'mindmap'" class="w-100 bg-card p-2">

         <div class="d-flex justify-content-end mb-2 align-items-center">
             <div class="mr-3 text-muted small">说明：脑图可以拖动和放大缩小，调整合适再下载。</div>
             <b-form-radio-group
                v-model="chartType"
                :options="[{text: '树状图', value: 'tree'}, {text: '桑基图', value: 'sankey'}]"
                buttons
                button-variant="outline-primary"
                size="sm"
             ></b-form-radio-group>
         </div>
         <v-chart v-if="currentOption" :options="currentOption" autoresize :style="mindMapContainerStyle" />
         <div v-else class="text-center p-5 text-muted">暂无数据</div>
      </div>

      <!-- Detail Modal -->
      <b-modal v-model="showDetailModal" size="xl" hide-footer centered body-class="p-0">
         <template #modal-title>
             <div v-if="selectedStock" class="d-flex align-items-center">
                 <span class="mr-2 font-weight-bold">{{ selectedStock.stock_name }}</span>
                 <span class="text-secondary small">{{ selectedStock.stock_code }}</span>
             </div>
             <div v-else>详情</div>
         </template>
         <div style="height: 70vh; overflow-y: auto;">
            <detail-content 
                v-if="selectedStock" 
                :detail-data="detailData" 
                :loading="detailLoading" 
                :stock="selectedStock" 
                :date="date1"
                :is-mobile="isMobile"
                @close="showDetailModal = false"
            ></detail-content>
         </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import DetailContent from './LhbDetailContent.vue'
import WxShare from '@/components/share/WxShare.vue'

export default {
  components: { DetailContent, WxShare },
  data() {
    return {
      date1: new Date().toISOString().slice(0, 10),
      lhbList: [],
      selectedStock: null,
      showDetailModal: false,
      detailLoading: false,
      detailData: null,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      loading: false,
      viewMode: 'list', // 'list' or 'mindmap'
      chartType: 'tree', // 'tree' or 'sankey'
      selectedSeats: [],
      showBuySellCounts: false,
      sortKey: 'gain',
      sortOrder: 'desc',
      seatCategoryMap: {
        '机构专用': '机构',
        '深股通专用': '北向',
        '沪股通专用': '北向'
      }
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768
    },
    filterStyle() {
      if (this.viewMode === 'list' && !this.isMobile) {
        return 'max-width: 1200px; margin: 0 auto;'
      }
      return 'width: 100%;'
    },
    allSeats() {
      if (!this.lhbList || this.lhbList.length === 0) return []
      const stats = {}
      
      this.lhbList.forEach(item => {
        const stockSeatNet = {} // seatName -> netAmount

        if (item.buy_group_icons) {
            item.buy_group_icons.forEach(i => {
                if (i.name) {
                    if (!stockSeatNet[i.name]) stockSeatNet[i.name] = 0
                    stockSeatNet[i.name] += Number(i.amount || 0)
                }
            })
        }
        
        if (item.sell_group_icons) {
            item.sell_group_icons.forEach(i => {
                if (i.name) {
                    if (!stockSeatNet[i.name]) stockSeatNet[i.name] = 0
                    stockSeatNet[i.name] -= Number(i.amount || 0)
                }
            })
        }
        
        // Aggregate to global stats
        Object.keys(stockSeatNet).forEach(name => {
            if (!stats[name]) stats[name] = { buy: 0, sell: 0 }
            const net = stockSeatNet[name]
            if (net > 0) {
                stats[name].buy += 1
            } else if (net < 0) {
                stats[name].sell += 1
            }
            // If net is exactly 0, we treat it as neutral/no-op for direction count, 
            // or we could check if they participated at all. 
            // But usually we care about directional bets. 
            // If net is 0 (T+0 with equal amt), maybe we should count it? 
            // For now let's stick to strict inequality to avoid noise, 
            // or default to Buy if we want to count participation.
            // Let's add to 'buy' if 0? No, let's ignore 0 net for now as it's rare.
        })
      })
      
      const sorted = Object.keys(stats).sort()
      return sorted.map(name => {
          let text = name
          if (this.isMobile && name.length > 4) {
              text = name.substring(0, 4)
          }
          
          const s = stats[name]
          const total = s.buy + s.sell
          let totalHtml = total
          if (s.buy > s.sell) {
              totalHtml = `<span class="text-danger font-weight-bold">${total}</span>`
          }
          // Construct HTML
          let html
          if (this.isMobile || !this.showBuySellCounts) {
              // Mobile or Switch Off: Name(Total)
              html = `${text}(${totalHtml})`
          } else {
              // PC and Switch On: Name Buy+Sell=Total
              html = `${text} <span class="text-danger font-weight-bold">${s.buy}</span>+<span class="text-success font-weight-bold">${s.sell}</span>=${totalHtml}`
          }
          
          return { html, value: name }
      })
    },
    allSelected() {
        return this.selectedSeats.length === this.allSeats.length
    },
    indeterminate() {
        return this.selectedSeats.length > 0 && this.selectedSeats.length < this.allSeats.length
    },
    filteredLhbList() {
      if (!this.lhbList) return []
      if (this.allSeats && this.allSeats.length > 0 && this.selectedSeats.length === 0) return []
      // If all selected or no seats filter, return all (optimization)
      let base = this.lhbList
      if (this.allSeats && this.allSeats.length > 0 && this.selectedSeats.length !== this.allSeats.length) {
        const selectedSet = new Set(this.selectedSeats)
        base = this.lhbList.filter(item => {
          const itemSeats = []
          if (item.buy_group_icons) item.buy_group_icons.forEach(i => i.name && itemSeats.push(i.name))
          if (item.sell_group_icons) item.sell_group_icons.forEach(i => i.name && itemSeats.push(i.name))
          return itemSeats.some(name => selectedSet.has(name))
        })
      }
      if (!this.sortKey) return base
      const key = this.sortKey
      const dir = this.sortOrder === 'asc' ? 1 : -1
      return base.slice().sort((a, b) => {
        const va = this.getSortValue(a, key)
        const vb = this.getSortValue(b, key)
        if (va === vb) return 0
        return va > vb ? dir : -dir
      })
    },
    statistics() {
      if (!this.filteredLhbList || this.filteredLhbList.length === 0) return null;
      
      let totalNetBuy = 0;
      let totalTurnover = 0;
      let totalCap = 0;
      let totalCircCap = 0;
      const count = this.filteredLhbList.length;

      this.filteredLhbList.forEach(item => {
        totalNetBuy += Number(item.buy_in || 0);
        totalTurnover += Number(item.turnover || 0);
        totalCap += Number(item.capitalization || 0);
        totalCircCap += Number(item.circ_price || 0);
      });

      return {
        netBuy: totalNetBuy,
        turnover: totalTurnover,
        totalCap: totalCap,
        circCap: totalCircCap,
        avgTurnover: count > 0 ? totalTurnover / count : 0,
        avgTotalCap: count > 0 ? totalCap / count : 0,
        avgCircCap: count > 0 ? totalCircCap / count : 0
      };
    },
    mindMapContainerStyle() {
      // Responsive width: Mobile 98%, PC 90%
      const w = this.isMobile ? '98%' : '85%'

      if (this.chartType === 'sankey') {
         const h = this.getSankeyHeight()
         return { width: w, height: h + 'px' }
      }
      
      const h = this.getTreeHeight()
      return { width: w, height: h + 'px' }
    },
    currentOption() {
      if (this.chartType === 'sankey') {
        return this.sankeyOption
      }
      return this.treeOption
    },
    sankeyOption() {
      if (!this.filteredLhbList || this.filteredLhbList.length === 0) return null
      
      const links = []
      const usedNodes = new Set()
      const stockNames = new Set(this.filteredLhbList.map(s => s.stock_name))
      
      this.filteredLhbList.forEach(stock => {
          // Process Seats
          const processSeat = (seats, type) => {
              if (!seats) return
              seats.forEach(seat => {
                  if (!seat || !seat.name) return
                  
                  const displayName = this.getSeatDisplayName(seat.name)

                  // Valid link found
                  usedNodes.add(displayName)
                  usedNodes.add(stock.stock_name)
                  
                  // Link
                  // Simplified: Always Seat -> Stock
                  // Color: Red for Buy, Green for Sell
                  // Value: Amount
                  links.push({
                      source: displayName,
                      target: stock.stock_name,
                      value: Math.abs(seat.amount),
                      lineStyle: {
                          color: type === 'buy' ? '#ff4d4f' : '#52c41a',
                          opacity: 0.4
                      },
                      tooltip: {
                          formatter: `${displayName} ${type === 'buy'?'买入':'卖出'} ${stock.stock_name}<br/>金额: ${this.formatMoney(Math.abs(seat.amount))}`
                      }
                  })
              })
          }
          
          processSeat(stock.buy_group_icons, 'buy')
          processSeat(stock.sell_group_icons, 'sell')
      })
      
      if (links.length === 0) return null

      const nodes = Array.from(usedNodes).map(name => {
          return { 
              name: name, 
              itemStyle: { 
                  color: stockNames.has(name) ? '#1890ff' : '#fa8c16' 
              } 
          }
      })
      
      return {
          tooltip: {
              trigger: 'item',
              triggerOn: 'mousemove'
          },
          toolbox: {
            feature: {
              saveAsImage: { show: true, title: '保存为图片' }
            }
          },
          graphic: this.getWatermarks(),
          series: [{
              type: 'sankey',
              data: nodes,
              links: links,
              emphasis: {
                  focus: 'adjacency'
              },
              nodeAlign: 'left',
              layoutIterations: 32, // Improve layout
              lineStyle: {
                  color: 'source',
                  curveness: 0.5
              },
              label: {
                  color: '#333',
                  fontSize: 12
              },
              // Shrink width by increasing padding
              left: '25%',
              right: '35%',
              top: '10%',
              bottom: '10%'
          }]
      }
    },
    treeOption() {
      if (!this.filteredLhbList || this.filteredLhbList.length === 0) return null

      // Group by seat name
      // seatName -> [{stockName, quoteChange, upDesc, buyIn, seatAmount}]
      let seatMap = {}

      this.filteredLhbList.forEach(stock => {
        if (stock.buy_group_icons) {
          stock.buy_group_icons.forEach(icon => {
            if (!seatMap[icon.name]) {
              seatMap[icon.name] = []
            }
            seatMap[icon.name].push({
              stockName: stock.stock_name,
              stockCode: stock.stock_code,
              quoteChange: stock.quote_change,
              upDesc: stock.up_desc,
              buyIn: stock.buy_in,
              seatAmount: icon.amount,
              turnover: stock.turnover,
              buySeats: stock.buy_group_icons || [],
              sellSeats: stock.sell_group_icons || [],
              seatType: 'buy',
              concepts: stock.concepts
            })
          })
        }
        if (stock.sell_group_icons) {
          stock.sell_group_icons.forEach(icon => {
            if (!seatMap[icon.name]) {
              seatMap[icon.name] = []
            }
            seatMap[icon.name].push({
              stockName: stock.stock_name,
              stockCode: stock.stock_code,
              quoteChange: stock.quote_change,
              upDesc: stock.up_desc,
              buyIn: stock.buy_in,
              seatAmount: -Math.abs(icon.amount || 0),
              turnover: stock.turnover,
              buySeats: stock.buy_group_icons || [],
              sellSeats: stock.sell_group_icons || [],
              seatType: 'sell',
              concepts: stock.concepts
            })
          })
        }
      })

      // Calculate Weekday
      const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const dayIndex = new Date(this.date1).getDay()
      const weekDayStr = weekDays[dayIndex]

      // Build children for root
      let children = []

      // Grouping buckets
      const targetCategories = ['一线游资', '顶级游资', '知名游资', '量化']
      const groupedNodes = {}
      targetCategories.forEach(c => groupedNodes[c] = [])
      const otherNodes = []

      for (let seatName in seatMap) {
        let stocks = seatMap[seatName]
        let stockChildren = stocks.map(s => {
          // Format label
          // name: Stock Name
          let stockLabel = `{name|${s.stockName}(${s.stockCode})}`
          const t = Number(s.turnover || 0)
          
          // Helper to build line
          const buildLine = (name, amount, type) => {
             const typeStyle = type === 'buy' ? 'typeBuy' : 'typeSell'
             const typeText = type === 'buy' ? '买' : '卖'
             const amtText = this.formatMoney(Math.abs(amount || 0))
             const pct = (t > 0 && amount) ? (Math.abs(amount) / t * 100).toFixed(1) : '0.0'
             const pctText = `占${pct}%`
             // Net buy moved to info node
             
             // {seat|Name} {type|Buy/Sell} {amt|Amount} {pct|Pct}
             return `{seat|${name}}{${typeStyle}|${typeText}}{amt|${amtText}}{pct|${pctText}}`
          }

          const detailChildren = []
          
          // 1. Add Info Node (Stock Metadata)
          const netColor = s.buyIn > 0 ? '#e33e33' : '#17803d'
          
          // Process concepts
          let conceptsStr = ''
          if (s.concepts) {
            const concepts = s.concepts.split(',').map(c => {
                const parts = c.split(':')
                return parts.length > 1 ? parts[1] : parts[0]
            }).slice(0, 5)
            conceptsStr = concepts.join(' ')
          }
          
          const infoNodeName = `{desc|${s.upDesc || ''}} {val|${s.quoteChange}%} {net|净${this.formatMoney(s.buyIn)}}` + (conceptsStr ? ` {c|${conceptsStr}}` : '')

          detailChildren.push({
              name: infoNodeName,
              value: 0,
              label: {
                  formatter: '{b}',
                  backgroundColor: '#fff0f6', // Pink background
                  borderColor: '#ffadd2',     // Pink border
                  borderWidth: 1,
                  borderRadius: 4,
                  padding: [2, 8],
                  align: 'left',
                  rich: {
                      desc: { fontSize: 11, color: '#666', padding: [0, 10, 0, 0] },
                      val: { fontSize: 12, fontWeight: 'bold', color: s.quoteChange > 0 ? '#e33e33' : '#17803d', padding: [0, 10, 0, 0] },
                      net: { fontSize: 11, color: netColor, fontWeight: 'bold' },
                      c: { fontSize: 11, color: '#666', padding: [0, 0, 0, 10] }
                  }
              }
          })
          
          // Helper to create child node
          const createDetailNode = (name, amount, type) => {
              return {
                  name: buildLine(name, amount, type),
                  value: amount,
                  label: {
                      formatter: '{b}',
                      backgroundColor: '#fff',
                      borderColor: '#eee',
                      borderWidth: 1,
                      borderRadius: 4,
                      padding: [1, 7],
                      align: 'left',
                      rich: {
                          seat: { width: 75, align: 'left', fontSize: 11, fontWeight: 'bold', color: '#333' },
                          typeBuy: { width: 16, align: 'center', color: '#e33e33', fontWeight: 'bold', fontSize: 11 },
                          typeSell: { width: 16, align: 'center', color: '#17803d', fontWeight: 'bold', fontSize: 11 },
                          amt: { width: 60, align: 'right', fontFamily: 'monospace', fontWeight: 'bold', fontSize: 11, color: '#333' },
                          pct: { width: 45, align: 'right', color: '#999', fontSize: 10 }
                      }
                  }
              }
          }

          // Main seat line
          detailChildren.push(createDetailNode(seatName, s.seatAmount, s.seatType))

          if (Array.isArray(s.buySeats)) {
            s.buySeats.forEach(ic => {
              if (!ic || !ic.name) return
              if (ic.name === seatName) return
              detailChildren.push(createDetailNode(ic.name, ic.amount, 'buy'))
            })
          }
          if (Array.isArray(s.sellSeats)) {
            s.sellSeats.forEach(ic => {
              if (!ic || !ic.name) return
              if (ic.name === seatName) return
              detailChildren.push(createDetailNode(ic.name, ic.amount, 'sell'))
            })
          }

          // 3. Add Concepts Node (Last Node)
          // Removed as per request, merged into Info Node

          return {
            name: stockLabel,
            value: s.seatAmount,
            label: {
                formatter: '{b}',
                backgroundColor: '#f8f9fa',
                borderColor: '#ddd',
                borderWidth: 1,
                borderRadius: 4,
                padding: [1, 8],
                align: 'left',
                rich: {
                    name: { fontSize: 12, fontWeight: 'normal', color: '#333' }
                }
            },
            // Add a transparent intermediate node to shorten the line?
            // No, just use children directly but adjust layout
            children: detailChildren
          }
        })
        
        // Group Logic
        const category = this.seatCategoryMap[seatName]
        const seatNode = {
          name: (category && targetCategories.includes(category)) ? seatName : this.getSeatDisplayName(seatName),
          children: stockChildren.sort((a, b) => Math.abs(b.value) - Math.abs(a.value)),
          label: {
              fontWeight: 'bold',
              fontSize: 14
          },
          collapsed: ['量化基金', '量化打板'].includes(seatName)
        }

        if (category && targetCategories.includes(category)) {
            groupedNodes[category].push(seatNode)
        } else {
            otherNodes.push(seatNode)
        }
      }

      // Build Children Array
      // 1. Add Categories
      targetCategories.forEach(cat => {
          const nodes = groupedNodes[cat]
          if (nodes.length > 0) {
              nodes.sort((a, b) => b.children.length - a.children.length)
              children.push({
                  name: cat,
                  children: nodes,
                  label: {
                      fontSize: 16,
                      fontWeight: 'bold',
                      backgroundColor: '#e6f7ff',
                      borderColor: '#1890ff',
                      borderWidth: 1,
                      borderRadius: 4,
                      padding: [4, 8],
                      color: '#000'
                  },
                  collapsed: false
              })
          }
      })

      // 2. Add Others to '其他游资'
      if (otherNodes.length > 0) {
        otherNodes.sort((a, b) => b.children.length - a.children.length)
        children.push({
            name: '其他游资',
            children: otherNodes,
            label: {
                fontSize: 16,
                fontWeight: 'bold',
                backgroundColor: '#e6f7ff',
                borderColor: '#1890ff',
                borderWidth: 1,
                borderRadius: 4,
                padding: [4, 8],
                color: '#000'
            },
            collapsed: false
        })
      }

      return {
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          formatter: (params) => {
              // Simple tooltip for now
              return params.name.replace(/\n/g, '<br/>')
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { show: true, title: '保存为图片' }
          }
        },
        graphic: this.getWatermarks(),
        series: [
          {
            type: 'tree',
            zoom: 1,
            edgeShape: 'polyline',
            edgeForkPosition: '63%',
            nodePadding: 2,
            layerPadding: 20, // Decrease layer padding to shorten lines
            data: [{
              name: `龙虎榜 \n${this.date1 || ''}\n ${weekDayStr}`,
              children: children,
              label: {
                  show: true,
                  fontSize: 15,
                  fontWeight: 'bold',
                  backgroundColor: '#e6f7ff',
                  borderColor: '#1890ff',
                  borderWidth: 1,
                  borderRadius: 4,
                  padding: [4, 8],
                  lineHeight: 20,
                  align: 'center'
              },
              symbolSize: 10
            }],
            top: '2%',
            left: '10%', // Increase left margin for root node
            bottom: '2%',
            right: '25%', // Increase right margin to squeeze width
            symbolSize: 2,
            roam: true, // Enable zoom and pan
            initialTreeDepth: this.isMobile ? 3 : 6,
            label: {
              position: 'left',
              verticalAlign: 'middle',
              align: 'right',
              fontSize: 12
            },
            leaves: {
              label: {
                position: 'right',
                verticalAlign: 'middle',
                align: 'left',
                padding: [2, 2]
              }
            },
            expandAndCollapse: true,
            animationDuration: 550,
            animationDurationUpdate: 750
          }
        ]
      }
    },
    shareTitle() {
      return `龙虎榜分析 (${this.date1}) - 自在量化`
    },
    shareDesc() {
      const count = this.lhbList ? this.lhbList.length : 0
      return `截止 ${this.date1}，共监控到 ${count} 只股票上榜。包含游资/机构买卖席位详情、榜单脑图和多维度资金流向过滤分析。`
    },
    shareLink() {
      const origin = window.location.origin
      return `${origin}/review/uplimit/lhb?date=${this.date1}`
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    if (this.$route.query.date) {
        this.date1 = this.$route.query.date
        this.fetchList()
    } else {
        this.setDefaultTradeDateByRule()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    getSankeyHeight() {
         const winH = this.windowHeight
         // Sankey height needs to be large enough for all nodes
         // Reduce height even more: 15px per node
         const estimated = this.lhbList.length * 15 + 100
         // Limit max height to 0.8 screen height (was 1.5)
         return Math.max(300, Math.min(Math.floor(winH * 0.8), estimated))
    },
    getTreeHeight() {
        let seatSet = new Set()
        this.lhbList.forEach(stock => {
            if (stock.buy_group_icons) {
                stock.buy_group_icons.forEach(icon => {
                    if (icon && icon.name) seatSet.add(icon.name)
                })
            }
            if (stock.sell_group_icons) {
                stock.sell_group_icons.forEach(icon => {
                    if (icon && icon.name) seatSet.add(icon.name)
                })
            }
        })
        
        let totalLines = 0
        this.lhbList.forEach(stock => {
            const n = (stock.buy_group_icons ? stock.buy_group_icons.length : 0) + 
                      (stock.sell_group_icons ? stock.sell_group_icons.length : 0)
            if (n > 0) totalLines += n * n
        })

        const seats = Math.max(1, seatSet.size)
        const winH = this.windowHeight
        
        // Height estimation:
        // seats * gap + totalLines * line_height
        // Compact height to avoid "too big" look
        const estimated = seats * 15 + totalLines * 12
        
        const minH = Math.max(400, Math.floor(winH * 0.5))
        const maxH = Math.floor(winH * 5.0) // Allow very tall trees if needed
        return Math.max(minH, Math.min(maxH, estimated))
    },
    getWatermarks() {
       return [{
           type: 'text',
           z: 100,
           silent: true,
           left: 'center',
           top: 'center',
           rotation: Math.PI/6,
           style: {
               fill: 'rgba(0, 0, 0, 0.1)',
               text: '自在量化\nquant.zizizaizai.com',
               font: 'bold 30px sans-serif',
               textAlign: 'center'
           }
       }]
    },
    onResize() {
        this.windowWidth = window.innerWidth
        this.windowHeight = window.innerHeight
    },
    fetchList() {
      this.loading = true
      this.selectedSeats = []
      this.seatCategoryMap = {
         '机构专用': '机构',
         '深股通专用': '北向',
         '沪股通专用': '北向'
      }
      request_zzquant({
        url: '/market/lhb/list',
        method: 'get',
        params: {
          date1: this.date1
        }
      }).then(res => {
        const data = res.data
        if (data.code === 20000) {
          this.lhbList = data.data || []
          
          // Build Seat Category Map dynamically
          this.lhbList.forEach(item => {
             // Buy Seats
             if (item.buy_group_icons) {
                item.buy_group_icons.forEach(seat => {
                    if (seat && seat.name) {
                        if (seat.youzi_icon) {
                            const map = {1: '一线游资', 2: '顶级游资', 3: '知名游资'}
                            this.seatCategoryMap[seat.name] = map[seat.youzi_icon] || ''
                        } else if (seat.tags) {
                            // Check for specific tags
                            const tags = seat.tags.split(',')
                            for(let t of tags) {
                                if (['顶级游资', '一线游资', '知名游资', '量化'].includes(t)) {
                                    this.seatCategoryMap[seat.name] = t
                                    break
                                }
                            }
                        }
                        
                        // Override for Quant
                        if (seat.name.indexOf('量化') !== -1) {
                            this.seatCategoryMap[seat.name] = '量化'
                        }
                    }
                })
             }
             // Sell Seats
             if (item.sell_group_icons) {
                item.sell_group_icons.forEach(seat => {
                    if (seat && seat.name) {
                        if (seat.youzi_icon) {
                            const map = {1: '一线游资', 2: '顶级游资', 3: '知名游资'}
                            this.seatCategoryMap[seat.name] = map[seat.youzi_icon] || ''
                        } else if (seat.tags) {
                            const tags = seat.tags.split(',')
                            for(let t of tags) {
                                if (['顶级游资', '一线游资', '知名游资', '量化'].includes(t)) {
                                    this.seatCategoryMap[seat.name] = t
                                    break
                                }
                            }
                        }
                        
                        // Override for Quant
                        if (seat.name.indexOf('量化') !== -1) {
                            this.seatCategoryMap[seat.name] = '量化'
                        }
                    }
                })
             }
          })
          
        } else {
          this.lhbList = []
          this.$bvToast.toast(data.message || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      }).catch(err => {
        console.error(err)
        this.lhbList = []
        this.$bvToast.toast('网络请求失败', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      }).finally(() => {
        this.loading = false
      })
    },
    selectStock(item) {
      this.selectedStock = item
      this.showDetailModal = true
      this.fetchDetail(item.stock_code)
    },
    fetchDetail(stockCode) {
        this.detailLoading = true
        this.detailData = null
        request_zzquant({
            url: '/market/lhb/detail',
            params: {
                date1: this.date1,
                stock_code: stockCode
            }
        }).then(res => {
            this.detailLoading = false
            if (res.data.code === 20000) {
                this.detailData = res.data.data
            }
        }).catch(() => {
            this.detailLoading = false
        })
    },
    setDefaultTradeDateByRule() {
        const now = new Date()
        const today = new Date(now.getTime())
        // 交易所龙虎榜通常在收盘后 17:30~18:00 左右公布并入库
        // 18:00 之前默认定位到已有数据的上一交易日；18:00 之后默认定位到今日
        const targetDate = now.getHours() < 18
            ? new Date(now.getTime() - 24 * 3600 * 1000)
            : today
        const dayEnd = targetDate.toISOString().slice(0, 10)
        request_zzquant({
            url: '/v3/market/trade/days',
            params: {
                days: 1,
                day_end: dayEnd
            }
        }).then(res => {
            if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
                this.date1 = res.data.data[0]
            } else {
                this.date1 = dayEnd
            }
            this.fetchList()
        }).catch(() => {
            this.date1 = dayEnd
            this.fetchList()
        })
    },
    getDisplayConcepts(conceptsStr) {
        if (!conceptsStr) return []
        // conceptsStr is "ID:Name,ID:Name"
        let parts = conceptsStr.split(',')
        let display = []
        for (let i = 0; i < parts.length && i < 2; i++) { // Limit to 2
            let p = parts[i]
            let items = p.split(':')
            if (items.length > 1) {
                display.push(items[1]) // Name
            } else {
                display.push(p) // Fallback
            }
        }
        return display
    },
    parseConcept(reason) {
        if (!reason) return '--'
        // Extract readable concepts from strict strings like "日涨幅偏离值达7%的证券"
        // If it contains specific keywords, return simplified version
        let r = reason
        if (r.indexOf('日涨幅') !== -1) return '日涨幅偏离'
        if (r.indexOf('日跌幅') !== -1) return '日跌幅偏离'
        if (r.indexOf('换手率') !== -1) return '高换手'
        if (r.indexOf('振幅') !== -1) return '高振幅'
        
        // If it's a long string, just take first few chars or split
        if (r.length > 6) return r.substring(0, 6)
        
        return r
    },
    getColorClass(val) {
        if (val > 0) return 'text-danger'
        if (val < 0) return 'text-success'
        return ''
    },
    getColStyle(field) {
      if (!this.isMobile) {
        const pcWidths = {
          name: 'flex: 0 0 12%;',
          seats: 'flex: 0 0 14%;',
          concepts: 'flex: 0 0 14%;',
          gain: 'flex: 0 0 10%;',
          netbuy: 'flex: 0 0 10%;',
          turnover: 'flex: 0 0 8%;',
          total_cap: 'flex: 0 0 8%;',
          circ_cap: 'flex: 0 0 8%;',
          amp: 'flex: 0 0 8%;',
          rate: 'flex: 0 0 8%;'
        }
        return pcWidths[field] || ''
      } else {
        const mobileWidths = {
          name: 'flex: 0 0 auto; width: 70px;',
          seats: 'flex: 0 0 auto; width: 80px;',
          concepts: 'flex: 0 0 auto; width: 80px;',
          gain: 'flex: 0 0 auto; width: 60px;',
          netbuy: 'flex: 0 0 auto; width: 85px;',
          turnover: 'flex: 0 0 auto; width: 80px;',
          total_cap: 'flex: 0 0 auto; width: 80px;',
          circ_cap: 'flex: 0 0 auto; width: 80px;',
          amp: 'flex: 0 0 auto; width: 60px;',
          rate: 'flex: 0 0 auto; width: 60px;'
        }
        return mobileWidths[field] || ''
      }
    },
    getSeatDisplayName(name) {
        const category = this.seatCategoryMap[name]
        if (category) {
            return `${category}·${name}`
        }
        return name
    },
    formatMoney(val) {
        if (!val) return '0'
        let absVal = Math.abs(val)
        let res = ''
        if (absVal >= 100000000) {
            res = (absVal / 100000000).toFixed(2) + '亿'
        } else if (absVal >= 10000) {
            res = (absVal / 10000000).toFixed(2) + '千万'
        } else {
            res = absVal.toFixed(2)
        }
        if (val < 0) return '-' + res
        return res
    },
    toggleAll(checked) {
      this.selectedSeats = checked ? this.allSeats.map(i => i.value) : []
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
    },
    getSortValue(item, key) {
      const map = {
        gain: 'quote_change',
        netbuy: 'buy_in',
        turnover: 'turnover',
        total_cap: 'capitalization',
        circ_cap: 'circ_price',
        amp: 'amplitude',
        rate: 'turnover_ratio'
      }
      const field = map[key] || key
      const raw = item && item[field] != null ? item[field] : 0
      const num = Number(raw)
      return Number.isNaN(num) ? 0 : num
    },
    downloadTable() {
      const rows = this.filteredLhbList || []
      const header = ['股票名称','股票代码','涨幅','净买入','成交额','总市值','流通市值','振幅','换手率','交易席位','概念']
      const lines = [header.join(',')]
      rows.forEach(item => {
        let concepts = ''
        if (Array.isArray(item.concepts)) {
          concepts = item.concepts.join('|')
        } else if (item.concepts) {
          const parts = String(item.concepts).split(',')
          concepts = parts.map(p => {
            const seg = p.split(':')
            return seg.length > 1 ? seg.slice(1).join(':') : seg[0]
          }).join('|')
        }
        const buySeats = Array.isArray(item.buy_group_icons)
          ? item.buy_group_icons.map(i => `${i.name || ''}(+${this.formatMoney(i.amount)})`).filter(v => v && v !== '(+0)').join('|')
          : ''
        const sellSeats = Array.isArray(item.sell_group_icons)
          ? item.sell_group_icons.map(i => `${i.name || ''}(-${this.formatMoney(i.amount)})`).filter(v => v && v !== '(-0)').join('|')
          : ''
        const seats = `买:${buySeats} 卖:${sellSeats}`.trim()
        const vals = [
          item.stock_name || '',
          item.stock_code || '',
          item.quote_change != null ? `${item.quote_change}%` : '',
          this.formatMoney(item.buy_in),
          this.formatMoney(item.turnover),
          this.formatMoney(item.capitalization),
          this.formatMoney(item.circ_price),
          item.amplitude != null ? `${item.amplitude}%` : '',
          item.turnover_ratio != null ? `${item.turnover_ratio}%` : '',
          seats,
          concepts
        ]
        const escaped = vals.map(v => {
          const s = String(v).replace(/"/g, '""')
          return `"${s}"`
        })
        lines.push(escaped.join(','))
      })
      const csv = '\uFEFF' + lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const fileName = `龙虎榜_${this.date1 || ''}.csv`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      link.click()
      URL.revokeObjectURL(link.href)
    }
  },
  watch: {
      date1() {
          this.fetchList()
      },
      allSeats(val, oldVal) {
          // Helper to get signature of values
          const getSig = (arr) => {
              if (!arr) return ''
              return arr.map(i => i.value).sort().join('|')
          }
          // Only update if values changed (ignore text changes due to resize)
          if (getSig(val) !== getSig(oldVal)) {
              this.selectedSeats = val.map(i => i.value)
          }
      }
  }
}
</script>

<style scoped>
.text-danger { color: var(--bento-up, #ef4444) !important; }
.text-success { color: var(--bento-down, #22c55e) !important; }
.badge-outline-danger {
    color: var(--bento-up, #ef4444);
    border: 1px solid var(--bento-up, #ef4444);
    background: var(--bg-card);
}
.badge-outline-success {
    color: var(--bento-down, #22c55e);
    border: 1px solid var(--bento-down, #22c55e);
    background: var(--bg-card);
}

.cursor-pointer { cursor: pointer; }
.scale-8 { transform: scale(0.85); transform-origin: left center; display: inline-block; }
/* Scrollbar styling for webkit */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
.seat-filter-mobile >>> .custom-control-label {
    font-size: 0.75rem;
    padding-top: 2px;
}
.seat-filter-mobile >>> .custom-control {
    min-height: 1.2rem;
    padding-left: 1.2rem;
    margin-bottom: 0.25rem;
    margin-right: 0;
}
.seat-filter-mobile >>> .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #007bff;
    background-color: #007bff;
}
</style>
